/**
 * フロントエンドLoggerクラス
 *
 * シングルトンパターンでログ収集・バッチ送信を行う。
 *
 * Requirements:
 * - 4.1: シングルトンパターンで実装
 * - 4.2: debug、info、warn、error メソッドを提供
 * - 4.3: 各ログに timestamp、level、url、userAgent を自動付与
 * - 4.4: setUser/clearUser でユーザー情報を連携
 * - 4.5: 開発環境では console にも詳細ログを出力
 * - 4.6: 本番環境ではバッチ送信のみ
 * - 5.1: ログを内部バッファに蓄積
 * - 5.2: バッファに20件蓄積されたらバッチ送信
 * - 5.3: 30秒経過してバッファにログがあればバッチ送信
 * - 5.4: navigator.sendBeacon API を使用
 * - 5.5: sendBeacon が利用不可の場合は fetch でフォールバック
 * - 5.6: ページアンロード時にバッファ内の全ログを送信
 * - 5.7: errorレベルのログは即座に送信
 * - 10.5: オフライン時にローカルストレージにバッファリング
 * - 10.6: オンライン復帰時にバッファリングされたログを送信
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  url: string;
  userAgent: string;
  userId?: number;
  userRole?: string;
  eventType?: string;
  eventData?: Record<string, unknown>;
}

export interface LoggerOptions {
  isDevelopment?: boolean;
  batchSize?: number;
  flushIntervalMs?: number;
  apiEndpoint?: string;
}

interface LogEventOptions {
  eventType?: string;
  eventData?: Record<string, unknown>;
}

// Use the same base URL as api-client.ts
const API_BASE_URL = import.meta.env?.VITE_BACKEND_URL || '';

const DEFAULT_OPTIONS: Required<LoggerOptions> = {
  isDevelopment: import.meta.env?.DEV ?? false,
  batchSize: 20,
  flushIntervalMs: 30000,
  apiEndpoint: `${API_BASE_URL}/api/logs/batch`,
};

const OFFLINE_STORAGE_KEY = 'frontend_log_buffer';

export class FrontendLogger {
  private static instance: FrontendLogger | null = null;

  private buffer: LogEntry[] = [];
  private options: Required<LoggerOptions>;
  private userId?: number;
  private userRole?: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isInitialized = false;

  private constructor(options: LoggerOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.initialize();
  }

  /**
   * シングルトンインスタンスを取得する
   */
  public static getInstance(options?: LoggerOptions): FrontendLogger {
    if (!FrontendLogger.instance) {
      FrontendLogger.instance = new FrontendLogger(options);
    }
    return FrontendLogger.instance;
  }

  /**
   * シングルトンインスタンスをリセットする（テスト用）
   */
  public static resetInstance(): void {
    if (FrontendLogger.instance) {
      FrontendLogger.instance.dispose();
      FrontendLogger.instance = null;
    }
  }

  private initialize(): void {
    if (this.isInitialized) return;

    // 定期的なフラッシュタイマーを設定
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.options.flushIntervalMs);

    // ページアンロード時にバッファを送信
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush(true);
      });

      // オンライン復帰時にバッファを送信
      window.addEventListener('online', () => {
        this.sendOfflineBuffer();
        this.flush();
      });
    }

    // オフライン時に保存されたログを復元
    this.loadOfflineBuffer();

    this.isInitialized = true;
  }

  private dispose(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.isInitialized = false;
  }

  /**
   * ユーザー情報を設定する
   */
  public setUser(userId: number, userRole: string): void {
    this.userId = userId;
    this.userRole = userRole;
  }

  /**
   * ユーザー情報をクリアする
   */
  public clearUser(): void {
    this.userId = undefined;
    this.userRole = undefined;
  }

  /**
   * バッファを取得する（テスト用）
   */
  public getBuffer(): LogEntry[] {
    return [...this.buffer];
  }

  /**
   * デバッグログを記録する
   */
  public debug(message: string, options?: LogEventOptions): void {
    this.log('debug', message, options);
  }

  /**
   * 情報ログを記録する
   */
  public info(message: string, options?: LogEventOptions): void {
    this.log('info', message, options);
  }

  /**
   * 警告ログを記録する
   */
  public warn(message: string, options?: LogEventOptions): void {
    this.log('warn', message, options);
  }

  /**
   * エラーログを記録する（即座に送信）
   */
  public error(message: string, options?: LogEventOptions): void {
    this.log('error', message, options);
    // エラーは即座に送信
    this.flush(true);
  }

  private log(level: LogLevel, message: string, options?: LogEventOptions): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      userId: this.userId,
      userRole: this.userRole,
      eventType: options?.eventType,
      eventData: options?.eventData,
    };

    // 開発環境ではコンソールに出力
    if (this.options.isDevelopment) {
      this.logToConsole(level, message, entry);
    }

    // バッファに追加
    this.buffer.push(entry);

    // バッファサイズに達したら送信
    if (this.buffer.length >= this.options.batchSize) {
      this.flush();
    }
  }

  private logToConsole(level: LogLevel, message: string, entry: LogEntry): void {
    const consoleMethod = level === 'debug' ? 'log' : level;
    console[consoleMethod](`[${level.toUpperCase()}]`, message, entry);
  }

  /**
   * バッファをフラッシュして送信する
   */
  public flush(isUnload = false): void {
    if (this.buffer.length === 0) return;

    const logsToSend = [...this.buffer];
    this.buffer = [];

    // sendBeaconを使用（ページアンロード時にも確実に送信）
    const success = this.sendLogs(logsToSend, isUnload);

    if (!success) {
      // 送信失敗時はバッファに戻す
      this.buffer = [...logsToSend, ...this.buffer];

      // オフライン時はローカルストレージに保存
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.saveOfflineBuffer();
      }
    }
  }

  private sendLogs(logs: LogEntry[], useBeaconOnly = false): boolean {
    const payload = JSON.stringify({ logs });
    const endpoint = this.options.apiEndpoint;

    // sendBeaconを優先使用
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const success = navigator.sendBeacon(endpoint, blob);
      if (success) return true;
    }

    // sendBeacon失敗またはページアンロード以外の場合はfetchを使用
    if (!useBeaconOnly) {
      this.sendWithFetch(logs);
      return true;
    }

    return false;
  }

  private async sendWithFetch(logs: LogEntry[]): Promise<void> {
    try {
      await fetch(this.options.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs }),
        keepalive: true,
      });
    } catch (error) {
      // 送信失敗時はバッファに戻す
      this.buffer = [...logs, ...this.buffer];

      // オフライン時はローカルストレージに保存
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.saveOfflineBuffer();
      }

      if (this.options.isDevelopment) {
        console.error('Failed to send logs:', error);
      }
    }
  }

  private saveOfflineBuffer(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const existingBuffer = this.loadStoredBuffer();
      const combinedBuffer = [...existingBuffer, ...this.buffer];
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(combinedBuffer));
    } catch (error) {
      if (this.options.isDevelopment) {
        console.error('Failed to save offline buffer:', error);
      }
    }
  }

  private loadOfflineBuffer(): void {
    if (typeof localStorage === 'undefined') return;

    const storedBuffer = this.loadStoredBuffer();
    if (storedBuffer.length > 0) {
      this.buffer = [...storedBuffer, ...this.buffer];
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
    }
  }

  private loadStoredBuffer(): LogEntry[] {
    if (typeof localStorage === 'undefined') return [];

    try {
      const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as LogEntry[];
      }
    } catch (error) {
      if (this.options.isDevelopment) {
        console.error('Failed to load offline buffer:', error);
      }
    }
    return [];
  }

  private sendOfflineBuffer(): void {
    const offlineBuffer = this.loadStoredBuffer();
    if (offlineBuffer.length > 0) {
      this.sendLogs(offlineBuffer);
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
    }
  }
}

// デフォルトエクスポート
export const logger = FrontendLogger.getInstance();
